import { queryOptions } from "@tanstack/react-query";
import sortBy from "lodash-es/sortBy";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { graphql } from "@/generated/digitalnisklady.cz";

// eslint-disable-next-line lingui/no-unlocalized-strings
const storageQueryDocument = graphql(`
  query Storage($id: ID!, $locale: String) {
    storage(id: $id) {
      id
      label
      zip
      address
      offers {
        edges {
          node {
            yearHarvested
            id
            amount
            totalAmount
            orders {
              edges {
                node {
                  amount
                  active
                }
              }
            }
            crop {
              id
              name(locale: $locale)
            }
          }
        }
      }
      company {
        id
        name
      }
    }
  }
`);

type Storage = z.infer<typeof storageSchema>;
const storageSchema = z.object({
  address: z.string(),
  company: z.object({
    id: z.string(),
    name: z.string(),
  }),
  id: z.string(),
  name: z.string(),
  offers: z.array(
    z.object({
      amount: z.number(),
      crop: z.object({
        id: z.string(),
        name: z.string(),
      }),
      harvestYear: z.number(),
      id: z.string(),
      orderAmount: z.number(),
      totalAmount: z.number(),
    }),
  ),
  postalCode: z.string(),
});

const storageQuery = (params: { id: string; locale: string }) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(storageQueryDocument, {
        ...params,
      }),
    queryKey: ["storage", params],
    select: (data) => {
      const result = {
        address: data.storage?.address,
        company: data.storage?.company,
        id: data.storage?.id,
        name: data.storage?.label,
        offers: data.storage?.offers?.edges
          ?.filter((edge) => edge?.node?.yearHarvested)
          .map((edge) => ({
            amount: edge?.node?.amount,
            crop: {
              id: edge?.node?.crop?.id,
              name: edge?.node?.crop?.name,
            },
            harvestYear: edge?.node?.yearHarvested,
            id: edge?.node?.id,
            orderAmount: edge?.node?.orders?.edges
              ?.filter((order) => order?.node?.active)
              .reduce((sum, order) => sum + (order?.node?.amount ?? 0), 0),
            totalAmount: edge?.node?.totalAmount,
          })),
        postalCode: data.storage?.zip,
      };

      const safeResult = storageSchema.parse(result);

      safeResult.offers = sortBy(safeResult.offers, (o) => [
        o.crop.name,
        o.harvestYear,
      ]);
      return safeResult;
    },
  });

export { storageQuery };
export type { Storage };
